import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Headphones, MessageCircle, MessageSquarePlus } from 'lucide-react'
import clsx from 'clsx'

import { FeedbackDialog } from '@/shared/components/support/FeedbackDialog'
import { SupportContactDialog } from '@/shared/components/support/SupportContactDialog'

export type SupportFloatVariant = 'default' | 'account'

type Props = {
  variant?: SupportFloatVariant
  className?: string
}

type Pos = { x: number; y: number }

const STORAGE_KEY = 'silkreels.supportFloat.pos'
const BTN = 44
const EDGE = 12
const DRAG_THRESHOLD = 6

function clampPos(x: number, y: number): Pos {
  if (typeof window === 'undefined') return { x, y }
  const maxX = Math.max(EDGE, window.innerWidth - BTN - EDGE)
  const maxY = Math.max(EDGE, window.innerHeight - BTN - EDGE)
  return {
    x: Math.min(maxX, Math.max(EDGE, x)),
    y: Math.min(maxY, Math.max(EDGE, y)),
  }
}

function defaultPos(variant: SupportFloatVariant): Pos {
  const bottomPad = variant === 'account' ? 32 : window.innerWidth >= 768 ? 32 : 80
  return clampPos(window.innerWidth - BTN - 16, window.innerHeight - BTN - bottomPad)
}

function loadPos(variant: SupportFloatVariant): Pos {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Pos
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        return clampPos(parsed.x, parsed.y)
      }
    }
  } catch {
    /* ignore */
  }
  return defaultPos(variant)
}

function savePos(pos: Pos) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos))
  } catch {
    /* ignore */
  }
}

export function SupportFloatDock({ variant = 'default', className }: Props) {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [pos, setPos] = useState<Pos>(() =>
    typeof window === 'undefined' ? { x: 0, y: 0 } : loadPos(variant),
  )
  const [dragging, setDragging] = useState(false)

  const rootRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
    moved: boolean
  } | null>(null)

  const openUpward = pos.y > 180

  const clampAndSet = useCallback((next: Pos) => {
    const clamped = clampPos(next.x, next.y)
    setPos(clamped)
    return clamped
  }, [])

  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p.x, p.y))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return
      setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      moved: false,
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    if (!drag.moved) {
      drag.moved = true
      setDragging(true)
      setMenuOpen(false)
    }
    clampAndSet({ x: drag.originX + dx, y: drag.originY + dy })
  }

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragRef.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    if (drag.moved) {
      setDragging(false)
      setPos((p) => {
        const clamped = clampPos(p.x, p.y)
        savePos(clamped)
        return clamped
      })
      return
    }
    setMenuOpen((open) => !open)
  }

  return (
    <>
      <div
        ref={rootRef}
        className={clsx('fixed z-[60]', className)}
        style={{ left: pos.x, top: pos.y, width: BTN, height: BTN }}
        aria-label={t('feedback.floatLabel')}
      >
        {menuOpen ? (
          <div
            role="menu"
            className={clsx(
              'absolute right-0 w-44 overflow-hidden rounded-xl border border-white/10 bg-[#14141d]/95 p-1 shadow-xl shadow-black/40 backdrop-blur-md',
              openUpward ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]',
            )}
          >
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm text-zinc-100 transition hover:bg-indigo-500/15"
              onClick={() => {
                setMenuOpen(false)
                setDialogOpen(true)
              }}
            >
              <MessageSquarePlus size={16} className="shrink-0 text-indigo-300" />
              <span>{t('feedback.floatFeedback')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm text-zinc-100 transition hover:bg-white/5"
              onClick={() => {
                setMenuOpen(false)
                setSupportOpen(true)
              }}
            >
              <Headphones size={16} className="shrink-0 text-zinc-400" />
              <span>{t('feedback.floatSupport')}</span>
            </button>
          </div>
        ) : null}

        <button
          type="button"
          title={`${t('feedback.floatLabel')} · ${t('feedback.floatDragHint')}`}
          aria-label={t('feedback.floatLabel')}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className={clsx(
            'flex h-11 w-11 touch-none items-center justify-center rounded-full border border-indigo-500/40 bg-card/95 text-indigo-200 shadow-lg shadow-black/30 backdrop-blur transition select-none',
            'hover:border-indigo-400/60 hover:bg-indigo-500/20',
            dragging ? 'cursor-grabbing scale-105' : 'cursor-grab',
            menuOpen && 'border-indigo-400/70 bg-indigo-500/25',
          )}
        >
          <MessageCircle size={20} />
        </button>
      </div>

      <FeedbackDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <SupportContactDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  )
}

export function supportFloatVariantFromPath(pathname: string): SupportFloatVariant {
  if (pathname.startsWith('/account')) return 'account'
  return 'default'
}
